import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Roles, RoleItem } from '@eview/core/models/roles';
import { User } from '@eview/core/models/user';
import { selectRoles } from '@eview/core/store/selectors/roles.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { UserService } from '@eview/core/users/user.service';
import { AuthCatalogFacade } from '@eview/core/store/facades/auth-catalog.facade';
import { FormService } from '@eview/core/domain/form/form.service';
import { Form } from '@eview/core/domain/form/form';
import {
  FORM_GRANT_ACTIONS,
  FORM_SCOPE_FAMILIES,
  FormGrant,
  FormGrantAction,
  GLOBAL_CAPABILITY_CODES
} from '@eview/core/auth/form-grants';

const LEGACY_GLOBAL_MIRROR: Record<string, string> = {
  'users.manage': 'User Management',
  'roles.manage': 'Access Management',
  'tags.manage': 'Access and edit incident indicators',
  'forms.configure':
    'Access and edit incident reports form - Names of reports'
};

const LEGACY_GLOBALS = Object.values(LEGACY_GLOBAL_MIRROR);

@Component({
  selector: 'eview-user-access-management',
  templateUrl: 'user-access.component.html',
  styleUrls: ['user-access.component.scss']
})
export class UserAccessComponent implements OnInit, OnDestroy {
  subs: Subscription;
  user: User;
  roles$: Observable<Roles>;
  roles: RoleItem[] = [];
  selectedRole: RoleItem | null = null;

  /** Global capability switches for selected role */
  globalToggles: Record<string, boolean> = {};
  globalCodes = GLOBAL_CAPABILITY_CODES;

  forms: Form[] = [];
  /** Full catalog — used when building the save payload. */
  formActions = FORM_GRANT_ACTIONS;
  /** UI: every family with Individual / General scope chips */
  scopedFormFamilies = FORM_SCOPE_FAMILIES;
  allForms = false;
  selectedFormIds: number[] = [];
  /** formId|'all' -> action -> bool */
  actionMatrix: Record<string, Record<string, boolean>> = {};

  saving = false;
  saveMessage = '';
  /** Optional API detail when save fails (422, missing table, etc.). */
  saveErrorDetail = '';

  constructor(
    private store: Store<AppState>,
    private userService: UserService,
    private authCatalog: AuthCatalogFacade,
    private formService: FormService
  ) {
    this.subs = new Subscription();
    this.roles$ = this.store.select(selectRoles);
  }

  ngOnInit() {
    this.authCatalog.ensureRoles();
    this.authCatalog.ensurePermissions();

    this.subs.add(
      this.formService.listForms().subscribe(data => {
        this.forms = (data?.results || []).filter(f => !f.disabled);
        if (this.selectedRole) {
          this.ensureActionMatrixDefaults();
        }
      })
    );

    this.subs.add(
      this.roles$.pipe(map(data => data?.results || [])).subscribe(list => {
        this.roles = (list || []).filter(r => r.name !== 'sysadmin' && r.name !== 'user');
        if (this.selectedRole) {
          const updated = this.roles.find(r => r.name === this.selectedRole.name);
          if (updated) {
            this.selectRole(updated);
          }
        } else if (this.roles.length) {
          this.selectRole(this.roles[0]);
        }
      })
    );

    this.subs.add(
      this.store.select(selectUser).subscribe(user => {
        this.user = user;
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  trackByRole(_index: number, role: RoleItem): string {
    return role?.name ?? String(_index);
  }

  trackByFormId(_index: number, form: Form): number {
    return form.id;
  }

  trackByAction(_index: number, action: FormGrantAction): string {
    return action;
  }

  trackByFamily(_index: number, family: { key: string }): string {
    return family.key;
  }

  /**
   * "Any" includes "own". Checking any forces own on (and locks the own chip).
   */
  onScopeAnyChange(
    matrix: Record<string, boolean>,
    family: { own: FormGrantAction; any: FormGrantAction },
    checked: boolean
  ): void {
    matrix[family.any] = checked;
    if (checked) {
      matrix[family.own] = true;
    }
  }

  onScopeOwnChange(
    matrix: Record<string, boolean>,
    family: { own: FormGrantAction; any: FormGrantAction },
    checked: boolean
  ): void {
    // While "any" is on, own is implied and cannot be turned off alone.
    if (matrix[family.any]) {
      matrix[family.own] = true;
      return;
    }
    matrix[family.own] = checked;
  }

  selectRole(role: RoleItem) {
    this.selectedRole = role;
    this.saveMessage = '';
    this.saveErrorDetail = '';
    this.globalToggles = {};
    for (const code of this.globalCodes) {
      this.globalToggles[code] =
        !!(role.permissions || []).includes(code as any) ||
        this.legacyGlobalPresent(role, code);
    }

    const grants: FormGrant[] = role.form_grants || [];
    this.allForms = grants.some(g => g.all_forms);
    this.selectedFormIds = grants
      .filter(g => !g.all_forms && g.form_id != null)
      .map(g => Number(g.form_id));
    this.actionMatrix = {};

    if (this.allForms) {
      const all = grants.find(g => g.all_forms);
      this.actionMatrix['all'] = this.actionsToMap(all?.actions || []);
    }
    for (const g of grants) {
      if (!g.all_forms && g.form_id != null) {
        this.actionMatrix[String(g.form_id)] = this.actionsToMap(g.actions || []);
      }
    }
    this.ensureActionMatrixDefaults();
  }

  private ensureActionMatrixDefaults(): void {
    for (const f of this.forms) {
      if (!this.actionMatrix[String(f.id)]) {
        this.actionMatrix[String(f.id)] = this.actionsToMap([]);
      }
    }
    if (!this.actionMatrix['all']) {
      this.actionMatrix['all'] = this.actionsToMap([]);
    }
  }

  private legacyGlobalPresent(role: RoleItem, code: string): boolean {
    const legacy = LEGACY_GLOBAL_MIRROR[code];
    return !!legacy && (role.permissions || []).includes(legacy as any);
  }

  private actionsToMap(actions: FormGrantAction[]): Record<string, boolean> {
    const m: Record<string, boolean> = {};
    for (const a of this.formActions) {
      m[a] = actions.includes(a);
    }
    // "any" implies "own" for display + save consistency
    for (const family of this.scopedFormFamilies) {
      if (m[family.any]) {
        m[family.own] = true;
      }
    }
    return m;
  }

  toggleFormSelection(formId: number, checked: boolean) {
    if (checked) {
      if (!this.selectedFormIds.includes(formId)) {
        this.selectedFormIds = [...this.selectedFormIds, formId];
      }
      if (!this.actionMatrix[String(formId)]) {
        this.actionMatrix[String(formId)] = this.actionsToMap([]);
      }
    } else {
      this.selectedFormIds = this.selectedFormIds.filter(id => id !== formId);
    }
  }

  isFormSelected(formId: number): boolean {
    return this.selectedFormIds.includes(formId);
  }

  onAllFormsChange(checked: boolean) {
    this.allForms = checked;
    if (checked && !this.actionMatrix['all']) {
      this.actionMatrix['all'] = this.actionsToMap([]);
    }
  }

  setScopeAllForms(all: boolean): void {
    this.onAllFormsChange(all);
  }

  saveSelectedRole() {
    if (!this.selectedRole) {
      return;
    }
    this.saving = true;
    this.saveMessage = '';
    this.saveErrorDetail = '';

    const nonGlobal = (this.selectedRole.permissions || []).filter(p => {
      const s = String(p);
      return (
        !this.globalCodes.includes(s) &&
        !LEGACY_GLOBALS.includes(s) &&
        s !== 'users.approve' &&
        s !== 'settings.manage' &&
        s !== 'Access and modify parameters settings' &&
        !s.includes('Conflictividad Electoral') &&
        !s.includes('Operaciones Electorales')
      );
    });

    const globalPermissions = this.globalCodes.filter(c => this.globalToggles[c]);
    const permissions: string[] = Array.from(
      new Set<string>([
        ...nonGlobal.map(p => String(p)),
        ...globalPermissions
      ])
    );

    for (const code of globalPermissions) {
      const legacy = LEGACY_GLOBAL_MIRROR[code];
      if (
        legacy &&
        (this.selectedRole.permissions || []).some(p => String(p) === legacy) &&
        !permissions.includes(legacy)
      ) {
        permissions.push(legacy);
      }
    }

    const form_grants: FormGrant[] = [];
    if (this.allForms) {
      const actions = this.formActions.filter(
        a => this.actionMatrix['all'] && this.actionMatrix['all'][a]
      );
      form_grants.push({ form_id: null, all_forms: true, actions });
    } else {
      for (const formId of this.selectedFormIds) {
        const key = String(formId);
        const actions = this.formActions.filter(
          a => this.actionMatrix[key] && this.actionMatrix[key][a]
        );
        form_grants.push({ form_id: formId, all_forms: false, actions });
      }
    }

    const roleBody: Partial<RoleItem> = {
      id: this.selectedRole.id,
      name: this.selectedRole.name,
      display_name: this.selectedRole.display_name,
      short_name: this.selectedRole.short_name,
      description: this.selectedRole.description,
      permissions: permissions as RoleItem['permissions']
    };

    this.userService.updateRoles(roleBody as RoleItem, this.selectedRole.id).subscribe({
      next: () => {
        this.userService
          .putRoleFormGrants(this.selectedRole.id ?? this.selectedRole.name, form_grants)
          .subscribe({
            next: () => {
              this.saving = false;
              this.saveMessage = 'saved';
              this.authCatalog.ensureRoles(true);
            },
            error: err => {
              this.saving = false;
              this.saveMessage = 'error';
              this.saveErrorDetail =
                this.extractApiError(err) ||
                'No se pudieron guardar los permisos por informe (¿migración role_form_grants aplicada?)';
            }
          });
      },
      error: err => {
        this.saving = false;
        this.saveMessage = 'error';
        this.saveErrorDetail =
          this.extractApiError(err) ||
          'No se pudo actualizar el rol (HTTP 422: permisos inválidos o no existentes en el catálogo).';
      }
    });
  }

  private extractApiError(err: unknown): string {
    const e = err as {
      error?: {
        errors?: Array<{ message?: string; title?: string } | string>;
        message?: string;
        messages?: Record<string, string[]>;
      };
      message?: string;
    };
    const body = e?.error;
    if (!body) {
      return '';
    }
    if (Array.isArray(body.errors) && body.errors.length) {
      const first = body.errors[0];
      if (typeof first === 'string') {
        return first;
      }
      return first?.message || first?.title || '';
    }
    if (body.messages && typeof body.messages === 'object') {
      const keys = Object.keys(body.messages);
      if (keys.length) {
        const msgs = body.messages[keys[0]];
        return Array.isArray(msgs)
          ? `${keys[0]}: ${msgs.join(', ')}`
          : String(msgs);
      }
    }
    return body.message || '';
  }
}
